import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import MainLayout from "@/components/layout/MainLayout";
import Seo from "@/components/base/Seo";
import { categories } from "@/mocks/categories";
import { mangaList, type Manga } from "@/mocks/manga";
import { formatNum, getCategory } from "@/utils/manga";
import MangaCard from "@/components/feature/MangaCard";

const sortOptions = [
  { key: "popularity", label: "人气最高" },
  { key: "rating", label: "评分最高" },
  { key: "new", label: "最近更新" },
] as const;

type SortKey = (typeof sortOptions)[number]["key"];

function applySort(list: Manga[], key: SortKey): Manga[] {
  const arr = [...list];
  if (key === "rating") return arr.sort((a, b) => b.rating - a.rating);
  if (key === "new") return arr.sort((a, b) => Number(b.isNew) - Number(a.isNew) || b.hot - a.hot);
  return arr.sort((a, b) => b.popularity - a.popularity);
}

export default function CategoryPage() {
  const { id } = useParams<{ id: string }>();
  const catId = id || "love";
  const cat = getCategory(catId);
  const [status, setStatus] = useState<"全部" | "连载中" | "已完结">("全部");
  const [sortKey, setSortKey] = useState<SortKey>("popularity");

  const works = useMemo(() => {
    let list = mangaList.filter((m) => m.category === catId);
    if (status !== "全部") list = list.filter((m) => m.status === status);
    return applySort(list, sortKey);
  }, [catId, status, sortKey]);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: `歪歪漫画网 ${cat.name}漫画分类大全`,
      url: `https://panelpal.github.io/category/${catId}`,
      inLanguage: "zh-CN",
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://panelpal.github.io/" },
        {
          "@type": "ListItem",
          position: 2,
          name: "分类大全",
          item: "https://panelpal.github.io/category/love",
        },
        {
          "@type": "ListItem",
          position: 3,
          name: `${cat.name}漫画`,
          item: `https://panelpal.github.io/category/${catId}`,
        },
      ],
    },
  ];

  return (
    <MainLayout>
      <Seo
        title={`歪歪漫画网${cat.name}漫画大全 - ${cat.tagline}`}
        description={`歪歪漫画网${cat.name}漫画分类大全，${cat.tagline}。${cat.desc}查看${cat.name}题材人气排行、新作与完结作品，免费漫画直达。`}
        keywords={`歪歪漫画网,${cat.name}漫画,${cat.name}漫画大全,${cat.name}漫画排行,${cat.name}漫画推荐`}
        canonical={`/category/${catId}`}
        jsonLd={jsonLd}
      />

      {/* 分类头图 */}
      <section className="relative overflow-hidden bg-foreground-900">
        <img
          src={cat.cover}
          alt={`${cat.name} 漫画分类大全`}
          className="absolute inset-0 h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/60 to-foreground-950/30"></div>
        <div className="relative z-10 mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
          <nav className="flex items-center gap-1.5 text-sm text-background-50/70" aria-label="面包屑">
            <Link to="/" className="hover:text-secondary-300">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to="/category/love" className="hover:text-secondary-300">分类大全</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-background-50">{cat.name}漫画</span>
          </nav>
          <div className="mt-5 flex items-center gap-4">
            <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-500 text-2xl text-background-50">
              <i className={cat.icon} />
            </span>
            <div>
              <h1 className="font-heading text-3xl text-background-50 md:text-4xl">{cat.name}漫画大全</h1>
              <p className="mt-1 text-sm text-background-50/80">
                {cat.tagline} · 收录 {formatNum(cat.count)} 部作品
              </p>
            </div>
          </div>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-background-50/90">{cat.desc}</p>
        </div>
      </section>

      {/* 分类内容 */}
      <section className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-1 rounded-full border border-background-200/80 bg-background-100 p-1">
            {(["全部", "连载中", "已完结"] as const).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setStatus(s)}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                  status === s ? "bg-primary-500 text-background-50" : "text-foreground-600 hover:text-foreground-900"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1 rounded-full border border-background-200/80 bg-background-100 p-1">
            {sortOptions.map((s) => (
              <button
                key={s.key}
                type="button"
                onClick={() => setSortKey(s.key)}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                  sortKey === s.key ? "bg-primary-500 text-background-50" : "text-foreground-600 hover:text-foreground-900"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {works.length === 0 ? (
          <div className="mt-8 rounded-lg border border-background-200/70 bg-background-100 py-16 text-center text-foreground-600">
            该筛选条件下暂无可展示的作品。
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-5">
            {works.map((m) => (
              <MangaCard key={m.id} manga={m} />
            ))}
          </div>
        )}
      </section>

      {/* 其他分类 */}
      <section className="border-t border-background-200/70 bg-background-100">
        <div className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
          <h2 className="font-heading text-xl text-foreground-950 md:text-2xl">其他热门分类</h2>
          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {categories
              .filter((c) => c.id !== catId)
              .map((c) => (
                <Link
                  key={c.id}
                  to={`/category/${c.id}`}
                  className="group flex items-center gap-3 rounded-lg border border-background-200/70 bg-background-50 p-3 transition-all duration-300 hover:-translate-y-0.5 hover:border-primary-200"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-lg text-primary-600">
                    <i className={c.icon} />
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-semibold text-foreground-950">{c.name}</span>
                    <span className="block truncate text-xs text-foreground-500">{formatNum(c.count)} 部作品</span>
                  </span>
                </Link>
              ))}
          </div>
        </div>
      </section>
    </MainLayout>
  );
}